import { Box, Button, Card, Grid, Stack } from "@mui/material";
import { FEED_CARD_WIDTH } from "app/appConstants";
import { MprcContainer, MprcRequestErrorReport } from "components";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { MprcReactPlayer } from "components/MprcReactPlayer";
import { FeedItemDto, FeedType } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { SessionService } from "services/sessionService";
import { PublicationFeed } from "views/knowledge/components/card";
import { subscribeShortVideoCompleted, unsubscribeShortVideoCompleted } from "views/knowledge/components/card/CardEvents";
import { CardManager } from "views/knowledge/components/card/CardManager";
import { CardSkeleton } from "views/knowledge/components/card/CardSkeleton";

export type FeedShortInitialFilter = {
    publication?: {
        publicationId: string;
        mediaFileId: string;
    };
};

export const FeedShortVideoExplorer: React.FC = () => {
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const [ds, setDataset] = useState<FeedItemDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [streamingLimitReached, setStreamingLimitReached] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(-1);
    const [viewError, setViewError] = useState<any>(null);
    const location = useLocation();

    ///
    const load = async () => {
        try {

            const usagesResponse = await api.v1.system.checkSystemUsages();
            if (!usagesResponse.data.streamingExceeded) {
                var result = await api.v1.knowledgeFeed.getFeedShorts({ startIndex: 0, endIndex: 149 });
                setDataset(result.data.items);
                if (result.data.totalRows > 0) {
                    let newIndex = 0;

                    const { publication } = (location.state ?? {}) as FeedShortInitialFilter;
                    if (publication) {
                        var index = result.data.items.findIndex(p => p.publicationId == publication.publicationId && p.attachs?.find(x => x.fileId == publication.mediaFileId) != null);
                        if (index > -1) newIndex = index;
                    }

                    setCurrentIndex(newIndex);
                }
            }
            else {
                setStreamingLimitReached(true);
            }
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    };

    const goNext = () => {
        if (currentIndex > - 1 && currentIndex < ds.length - 1) {
            setCurrentIndex(index => index + 1);
        }
    };

    const goPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(index => (index ?? 0) - 1);
        }
    };

    useDidUpdateEffect(() => {
        subscribeShortVideoCompleted(goNext);

        return () => {
            unsubscribeShortVideoCompleted(goNext);
        };
    }, [currentIndex]);

    useEffect(() => {
        load();
    }, []);

    ///
    const onDataItemChanged = (index: number, data: Partial<PublicationFeed>) => {
        setDataset(prevState => {
            const newState = prevState.map((obj, i) => {
                if (i == index) {
                    return { ...obj, ...data };
                }
                return obj;
            });
            return newState;
        });
    };

    const RenderCard = () => {
        const item = ds[currentIndex];

        return <Stack direction="column" justifyContent="space-between" alignItems="center" height="100%" width="100%">
            <Button
                variant="contained"
                fullWidth
                color="muted"
                onClick={goPrevious}
                sx={{ mb: 2, visibility: ds.length > 1 && currentIndex > 0 ? "visible" : "hidden" }}
            >
                <span>{t("Actions.GoPrevious", { ns: "shared" })}</span>
            </Button>


            <Box sx={(theme) => ({
                [theme.breakpoints.down("md")]: {
                    width: FEED_CARD_WIDTH,
                    margin: "0 auto"
                },
                minWidth: FEED_CARD_WIDTH
            })}>
                <CardManager
                    feedType={FeedType.ShortExplorer}
                    pub={item}
                    onNeedResetRowHeight={() => null}
                    onReload={() => null}
                    onFeedItemChanged={(item) => {
                        if (item.shortVideo?.played === true) goNext();
                        else onDataItemChanged(currentIndex, item);
                    }}
                />
            </Box>

            <Button
                variant="contained"
                fullWidth
                onClick={goNext}
                color="muted"
                sx={{ visibility: ds.length > 1 && currentIndex < ds.length - 1 ? "visible" : "hidden" }}
            >
                <span>{t("Actions.GoNext", { ns: "shared" })}</span>
            </Button>
        </Stack>;
    };

    const RenderCardLimitReached = () => {
        return <Stack direction="column" justifyContent="center" alignItems="center" height="100%" width="100%">
            <Box sx={(theme) => ({
                [theme.breakpoints.down("md")]: {
                    width: FEED_CARD_WIDTH,
                    margin: "0 auto"
                },
                minWidth: FEED_CARD_WIDTH
            })}>
                <Card>
                    <Box sx={{ minWidth: FEED_CARD_WIDTH }}>
                        <Grid container width="100%" direction="column" gap={1} padding={1}>
                            <Grid id="mediaSection" width="100%" position="relative">
                                <MprcReactPlayer
                                    id="VideoLimitReached"
                                    playing={true}
                                    playsinline={true}
                                    stopOnUnmount={true}
                                    url={SessionService.current.getApiFullUrl("/GetVideoPlanExceeded.mp4")!}
                                    width="100%"
                                    height="100%"
                                    controls={{
                                        seek: false,
                                        volume: false,
                                        speed: false,
                                        closeCaption: false,
                                        playTime: true,
                                        fullscreen: false,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Card>
            </Box>
        </Stack>;
    };

    return viewError
        ? <MprcRequestErrorReport error={viewError} />
        : (
            <Box
                sx={(theme) => ({
                    maxWidth: theme.breakpoints.values["xl"],
                    margin: "0 auto",
                    height: "100%"
                })}
            >
                <Box sx={{ width: FEED_CARD_WIDTH, margin: "0 auto", height: "100%" }}>
                    {
                        loading
                            ? <CardSkeleton visible />
                            : streamingLimitReached
                                ? RenderCardLimitReached()
                                : ds.length == 0
                                    ? (
                                        <MprcContainer>
                                            <MprcFilterEmptyDataset
                                                message={t("Feed.ShortVideoExplorer.EmptyDataset")}
                                            />
                                        </MprcContainer>
                                    )
                                    : currentIndex > -1 && RenderCard()
                    }
                </Box>
            </Box>
        );
};
